import React from 'react';
import PropTypes from 'prop-types';

/**
 * Wizard Dashboard Skeleton Loader
 * Single Responsibility: display skeleton cards while wizard status is loading.
 */

const CARD_SKELETON_CLASS =
  'bg-white flex min-h-[280px] sm:min-h-[320px] lg:min-h-[391px] items-center p-4 sm:p-5 lg:p-6 rounded-lg sm:rounded-[8px] w-full min-w-0 relative animate-pulse';
const BORDER_SKELETON_CLASS =
  'absolute border-2 sm:border-4 border-[#e5e5e5] border-solid inset-0 pointer-events-none rounded-lg sm:rounded-[8px] shadow-[0px_2px_20px_0px_rgba(0,0,0,0.1)]';
const SKELETON_BG = 'bg-[#e5e5e5]';
const HEADING_CLASS =
  'font-[\'Roboto:Bold\',sans-serif] font-bold text-[32px] md:text-[40px] text-black tracking-[0.16px]';
const HEADING_STYLE = { fontVariationSettings: "'wdth' 100" };

function CardSkeleton() {
  return (
    <div className={CARD_SKELETON_CLASS}>
      <div aria-hidden="true" className={BORDER_SKELETON_CLASS} />
      <div className="flex flex-col gap-3 sm:gap-4 lg:gap-[15px] items-start w-full min-w-0">
        <div className="flex flex-col gap-3 sm:gap-4 lg:gap-4 items-start w-full min-w-0">
          <div className={`h-7 w-10 sm:h-8 sm:w-[40px] ${SKELETON_BG} rounded-md shrink-0`} />
          <div className="flex flex-col gap-2 sm:gap-3 w-full min-w-0">
            <div className={`h-6 sm:h-7 w-full max-w-full ${SKELETON_BG} rounded-md`} />
            <div className={`h-6 sm:h-7 w-[90%] max-w-full ${SKELETON_BG} rounded-md`} />
            <div className={`h-4 sm:h-5 w-full max-w-full mt-1 ${SKELETON_BG} rounded-md`} />
          </div>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3 lg:gap-[14px] items-end w-full min-w-0">
          <div className={`h-9 sm:h-[37px] w-24 sm:w-[113px] min-w-0 ${SKELETON_BG} rounded-lg sm:rounded-[10px] shrink-0`} />
          <div className={`size-24 sm:size-32 lg:size-[163px] min-w-0 ${SKELETON_BG} rounded-lg shrink-0`} />
        </div>
      </div>
    </div>
  );
}

export function WizardDashboardSkeleton({ organizationName = '' }) {
  return (
    <div className="w-full">
      <div className="max-w-[1400px] mx-auto w-full">
        <div className="mb-8 md:mb-12">
          {organizationName ? (
            <h1 className={HEADING_CLASS} style={HEADING_STYLE}>
              {organizationName}
            </h1>
          ) : (
            <div className={`h-8 sm:h-9 md:h-10 w-40 sm:w-48 md:w-[200px] max-w-full ${SKELETON_BG} rounded-md animate-pulse`} />
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 lg:gap-6 xl:gap-8">
          <CardSkeleton key="skeleton-1" />
          <CardSkeleton key="skeleton-2" />
          <CardSkeleton key="skeleton-3" />
        </div>
      </div>
    </div>
  );
}

WizardDashboardSkeleton.propTypes = {
  organizationName: PropTypes.string,
};
